import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  api,
  ApiError,
  isLiveBotStatus,
  isTerminal,
  statusKind,
  statusLabel,
  templateLabel,
  type MeetingDetail,
  type TranscriptLine,
} from '../lib/api.js';
import { StatusBadge } from './LibraryPage.js';
import { currentBrand } from '../lib/brand.js';
import {
  formatDate,
  formatDuration,
  formatMsClock,
  platformLabel,
} from '../lib/format.js';
import { AskPanel, HighlightsPanel, ActionsPanel } from './MeetingExtras.js';
import { LedgerPanel } from './MeetingLedger.js';
import { DownloadAudioButton } from '../components/DownloadAudioButton.js';
import { DownloadVideoButton } from '../components/DownloadVideoButton.js';
import { AgentSeatPanel } from './AgentSeatPanel.js';

const POLL_MS = 4000;

export function MeetingPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [meeting, setMeeting] = useState<MeetingDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [currentMs, setCurrentMs] = useState(0);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [kicking, setKicking] = useState(false);
  const [kickDone, setKickDone] = useState(false);
  const [kickNote, setKickNote] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!id) return;
    setKickDone(false);
    setKickNote(null);
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | undefined;

    // setTimeout chain keyed off the latest response (the old setInterval read
    // a stale `meeting` closure and never stopped). Stops at terminal status.
    async function load(): Promise<void> {
      let keepPolling = true;
      try {
        const detail = await api.getMeeting(id!);
        if (!alive) return;
        setMeeting(detail);
        setError(null);
        // Keep polling while cloud copy is in progress, or until a summary lands
        // (status can flip to complete before the UI has refreshed with notes).
        keepPolling =
          !isTerminal(detail.status) ||
          !!detail.videoStoring ||
          (detail.status === 'complete' && !detail.summary);
      } catch (err) {
        if (!alive) return;
        setError(err instanceof Error ? err.message : String(err));
        keepPolling = !(err instanceof ApiError && (err.status === 401 || err.status === 404));
      }
      if (alive && keepPolling) timer = setTimeout(() => void load(), POLL_MS);
    }

    void load();
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
  }, [id]);

  const seek = useCallback((ms: number) => {
    const v = videoRef.current;
    if (!v) return;
    // Notes + transcript sit below the player; bring it back into view on seek.
    const r = v.getBoundingClientRect();
    if (r.bottom < 80 || r.top > window.innerHeight) {
      v.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    v.currentTime = Math.max(0, ms / 1000);
    void v.play().catch(() => { /* autoplay blocked — ignore */ });
  }, []);

  const onTimeUpdate = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    setCurrentMs(Math.floor(v.currentTime * 1000));
  }, []);

  async function doDelete(): Promise<void> {
    if (!id || deleting) return;
    setDeleting(true);
    try {
      await api.deleteMeeting(id);
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  }

  async function doKick(): Promise<void> {
    if (!id || kicking || kickDone) return;
    setKicking(true);
    setKickNote(null);
    try {
      const result = await api.leaveMeeting(id);
      setKickDone(true);
      if (result.awaitingMedia) {
        setKickNote('Notetaker removed. Saving the recording…');
      } else if (result.alreadyLeft && result.status !== 'bot_removed') {
        setKickNote('Notetaker is already out of this meeting.');
      } else {
        setKickNote('Notetaker removed from the meeting.');
      }
      const detail = await api.getMeeting(id);
      setMeeting(detail);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setKicking(false);
    }
  }

  if (error && !meeting) {
    return (
      <div>
        <Link to="/" className="back-link">&larr; Back to Library</Link>
        <div className="form-error" style={{ marginTop: 12 }}>{error}</div>
      </div>
    );
  }

  if (!meeting) {
    return (
      <div className="empty">
        <span className="spinner" /> Loading meeting&hellip;
      </div>
    );
  }

  const title = meeting.title ?? `${platformLabel(meeting.platform)} meeting`;
  const when = meeting.startedAt ?? null;

  return (
    <div>
      <Link to="/" className="back-link">
        &larr; Back to Library
      </Link>

      <div className="meeting-header">
        <div>
          <h1 className="page-title">
            {title}
            <StatusBadge
              kind={statusKind(meeting.status)}
              label={meeting.status === 'complete' && meeting.summary ? 'Notes ready' : statusLabel(meeting.status)}
            />
          </h1>
          <div className="meeting-header__meta">
            <span>{platformLabel(meeting.platform)}</span>
            <span>·</span>
            <span>{formatDate(when)}</span>
            <span>·</span>
            <span>{formatDuration(meeting.durationSeconds)}</span>
            {meeting.templateType && (
              <>
                <span>·</span>
                <span style={{ color: 'var(--accent)' }}>{templateLabel(meeting.templateType)}</span>
              </>
            )}
          </div>
        </div>
        <div className="meeting-header__actions">
          {meeting.shareUrl && <ShareHeaderActions url={meeting.shareUrl} />}
          {(meeting.videoStored || meeting.videoStoring || meeting.hasRecallVideo) && (
            <DownloadVideoButton
              meetingId={meeting.id}
              available={!!meeting.videoStored}
              preparing={!!meeting.videoStoring && !meeting.videoStored}
            />
          )}
          {(meeting.videoStored || meeting.audioStored) && (
            <DownloadAudioButton meetingId={meeting.id} audioStored={!!meeting.audioStored} />
          )}
          {isLiveBotStatus(meeting.status) && !kickDone && !(meeting.agentSeat?.sameBot && !meeting.agentSeat.stopped) && (
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => void doKick()}
              disabled={kicking}
              title="Remove the notetaker bot from this live meeting"
            >
              {kicking ? 'Removing…' : 'Remove notetaker'}
            </button>
          )}
          <button
            type="button"
            className="btn btn--danger-ghost"
            onClick={() => setDeleteConfirm(true)}
          >
            Delete
          </button>
        </div>
      </div>

      {deleteConfirm && (
        <div className="dialog-backdrop" role="dialog" aria-modal="true">
          <div className="dialog">
            <h2>Delete this meeting?</h2>
            <p>This cannot be undone.</p>
            <div className="dialog__actions">
              <button type="button" className="btn btn--ghost" onClick={() => setDeleteConfirm(false)} disabled={deleting}>
                Cancel
              </button>
              <button type="button" className="btn btn--danger" onClick={() => void doDelete()} disabled={deleting}>
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {meeting.errorMessage && (
        <div className="form-error" style={{ marginBottom: 16 }}>
          {meeting.errorMessage}
        </div>
      )}

      {kickNote && (
        <div className="form-success" style={{ marginBottom: 16 }}>
          {kickNote}
        </div>
      )}

      {id && (
        <AgentSeatPanel
          meetingId={id}
          status={meeting.status}
          seat={meeting.agentSeat}
          onChanged={async () => {
            const detail = await api.getMeeting(id);
            setMeeting(detail);
          }}
        />
      )}

      {!isTerminal(meeting.status) && (
        <div className="panel panel--status" style={{ marginBottom: 16 }}>
          <p className="panel__subtle" style={{ margin: 0 }}>
            <span className="spinner" />{' '}
            {meeting.status === 'bot_joining' && (
              meeting.agentSeat?.sameBot && !meeting.agentSeat.stopped
                ? 'AI is joining with voice and video.'
                : `${currentBrand().name} is joining the meeting…`
            )}
            {meeting.status === 'recording' && (
              kickDone
                ? 'Notetaker removed. Waiting for the recording to finish saving…'
                : meeting.agentSeat?.sameBot && !meeting.agentSeat.stopped
                  ? 'AI is in the meeting with voice and video. The call is still being recorded.'
                  : 'Recording in progress. Transcript will appear once the meeting ends.'
            )}
            {meeting.status === 'transcribing' && 'Meeting ended. Transcribing audio…'}
            {meeting.status === 'summarizing' && 'Transcript ready. Claude is writing the summary…'}
            {meeting.status === 'pending' && 'Queued.'}
          </p>
        </div>
      )}

      <div className="meeting-layout">
        <div>
          <VideoPanel
            videoUrl={meeting.videoUrl}
            videoRef={videoRef}
            onTimeUpdate={onTimeUpdate}
          />
          <ShareBar meeting={meeting} onUpdated={setMeeting} />
        </div>

        {/* Notes first: Summary + Highlights sit above the transcript. */}
        <div className="meeting-notes">
          <SummaryPanel meeting={meeting} seek={seek} />
          <LedgerPanel meetingId={meeting.id} meetingStatus={meeting.status} seek={seek} />
          <HighlightsPanel
            meetingId={meeting.id}
            seek={seek}
            transcriptLength={meeting.transcript.length}
          />
        </div>

        <TranscriptPanel
          meetingId={meeting.id}
          transcript={meeting.transcript}
          currentMs={currentMs}
          seek={seek}
          onSpeakersUpdated={(t) => setMeeting((m) => m ? { ...m, transcript: t } : m)}
        />

        <div className="meeting-tools">
          <AskPanel meetingId={meeting.id} enabled={meeting.transcript.length > 0} />
          <ActionsPanel
            meeting={meeting}
            onUpdated={() => {
              void api.getMeeting(meeting.id).then(setMeeting);
            }}
          />
        </div>
      </div>
    </div>
  );
}

/** Copy / Open share link — lives in the meeting header (Stitch meeting-v1). */
function ShareHeaderActions({ url }: { url: string }): JSX.Element {
  const [copied, setCopied] = useState(false);

  async function copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      window.prompt('Copy this share link', url);
      return;
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <>
      <button type="button" className="btn btn--ghost" onClick={() => void copy()} title={url}>
        {copied ? 'Copied' : 'Copy share link'}
      </button>
      <a className="btn" href={url} target="_blank" rel="noopener noreferrer">
        Open share link
      </a>
    </>
  );
}

function VideoPanel({
  videoUrl,
  videoRef,
  onTimeUpdate,
}: {
  videoUrl: string | null;
  videoRef: React.MutableRefObject<HTMLVideoElement | null>;
  onTimeUpdate: () => void;
}): JSX.Element {
  return (
    <div className="video-wrap">
      {videoUrl ? (
        <video
          ref={videoRef}
          src={videoUrl}
          controls
          playsInline
          preload="metadata"
          onTimeUpdate={onTimeUpdate}
        />
      ) : (
        <div className="video-placeholder">
          Recording will appear here once the meeting ends and media is processed.
        </div>
      )}
    </div>
  );
}

function ShareBar({
  meeting,
  onUpdated,
}: {
  meeting: MeetingDetail;
  onUpdated: (m: MeetingDetail) => void;
}): JSX.Element | null {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [requested, setRequested] = useState(false);

  // After "Store in cloud", poll until the share link shows up (the main
  // page poll stops at terminal statuses).
  useEffect(() => {
    if (!requested || meeting.shareUrl) return;
    let alive = true;
    let tries = 0;
    const tick = async (): Promise<void> => {
      tries += 1;
      try {
        const d = await api.getMeeting(meeting.id);
        if (!alive) return;
        onUpdated(d);
        if (d.shareUrl || (!d.videoStoring && tries > 3)) {
          setRequested(false);
          if (!d.shareUrl) setErr('Cloud copy did not finish. Try again.');
          return;
        }
      } catch {
        /* keep trying */
      }
      if (alive && tries < 150) timer = setTimeout(() => void tick(), 4000);
    };
    let timer: ReturnType<typeof setTimeout> | undefined = setTimeout(() => void tick(), 3000);
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
  }, [requested, meeting.id, meeting.shareUrl, onUpdated]);

  async function store(): Promise<void> {
    setBusy(true);
    setErr(null);
    try {
      const r = await api.storeVideo(meeting.id);
      if (r.alreadyStored || r.shareUrl) {
        onUpdated(await api.getMeeting(meeting.id));
      } else {
        setRequested(true);
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  // Saved: Copy / Open live in the meeting header.
  if (meeting.shareUrl) return null;

  if (meeting.videoStoring || requested) {
    return (
      <div className="panel share-strip" style={{ marginTop: 12 }}>
        <span className="panel__subtle">
          <span className="spinner" /> Saving to cloud…
        </span>
      </div>
    );
  }

  if (meeting.hasRecallVideo && meeting.cloudStorageConfigured) {
    return (
      <div className="panel share-strip" style={{ marginTop: 12 }}>
        <span className="panel__subtle" style={{ marginRight: 'auto' }}>
          {err ?? 'Recording not yet saved to cloud storage.'}
        </span>
        <button type="button" className="btn" onClick={() => void store()} disabled={busy}>
          {busy ? 'Starting…' : 'Store in cloud'}
        </button>
      </div>
    );
  }

  return null;
}

function TranscriptPanel({
  meetingId,
  transcript,
  currentMs,
  seek,
  onSpeakersUpdated,
}: {
  meetingId: string;
  transcript: TranscriptLine[];
  currentMs: number;
  seek: (ms: number) => void;
  onSpeakersUpdated: (t: TranscriptLine[]) => void;
}): JSX.Element {
  const speakers = useMemo(() => uniqueSpeakers(transcript), [transcript]);
  const activeIndex = useMemo(() => findActive(transcript, currentMs), [transcript, currentMs]);

  async function onRename(label: string, newName: string): Promise<void> {
    const name = newName.trim();
    if (!name) return;
    await api.renameSpeaker(meetingId, label, name);
    onSpeakersUpdated(
      transcript.map((t) => t.speakerLabel === label ? { ...t, speakerName: name } : t),
    );
  }

  if (transcript.length === 0) {
    return (
      <div className="panel">
        <h3 className="panel__title">Transcript</h3>
        <p className="panel__subtle">No transcript yet.</p>
      </div>
    );
  }

  return (
    <div className="panel">
      <h3 className="panel__title">
        Full transcript
        <span className="panel__subtle" style={{ fontWeight: 400, marginLeft: 8 }}>
          Click a line to jump the recording
        </span>
      </h3>

      {speakers.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
          {speakers.map((s) => (
            <SpeakerChip
              key={s.label}
              label={s.label}
              name={s.name}
              onRename={(newName) => onRename(s.label, newName)}
            />
          ))}
        </div>
      )}

      <div className="transcript">
        {transcript.map((line, idx) => (
          <div
            key={`${line.startMs}-${idx}`}
            className={`transcript-row${idx === activeIndex ? ' transcript-row--active' : ''}`}
            onClick={() => seek(line.startMs)}
          >
            <span className="transcript-row__time">{formatMsClock(line.startMs)}</span>
            <div className="transcript-row__body">
              <span className="transcript-row__speaker">
                {line.speakerName ?? `Speaker ${line.speakerLabel}`}
              </span>
              <span className="transcript-row__text">{line.text}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SpeakerChip({
  label,
  name,
  onRename,
}: {
  label: string;
  name: string | null;
  onRename: (newName: string) => void | Promise<void>;
}): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(name ?? '');

  if (editing) {
    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setEditing(false);
          if (draft.trim() && draft.trim() !== name) void onRename(draft);
        }}
        style={{ display: 'inline-flex', gap: 4 }}
      >
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => setEditing(false)}
          style={{ padding: '4px 8px', fontSize: 12, width: 140 }}
          placeholder={`Speaker ${label}`}
        />
      </form>
    );
  }

  return (
    <button
      type="button"
      className="speaker-chip"
      onClick={() => { setDraft(name ?? ''); setEditing(true); }}
      title="Click to rename"
    >
      <span className="speaker-chip__label">{label}</span>
      <span className="speaker-chip__name">{name ?? `Speaker ${label}`}</span>
    </button>
  );
}

function SummaryPanel({
  meeting,
  seek,
}: {
  meeting: MeetingDetail;
  seek: (ms: number) => void;
}): JSX.Element {
  const s = meeting.summary;
  const seatWork = meeting.seatWork ?? [];

  if (!s) {
    return (
      <div className="panel">
        <h3 className="panel__title">Summary</h3>
        <p className="panel__subtle">
          {isTerminal(meeting.status)
            ? 'No summary is available for this meeting.'
            : 'Will appear once Claude finishes summarizing.'}
        </p>
        {seatWork.length > 0 && <SeatWorkList items={seatWork} />}
      </div>
    );
  }

  const keyTopics = Array.isArray(s.keyTopics) ? s.keyTopics : [];
  const nextSteps = Array.isArray(s.nextSteps) ? s.nextSteps : [];

  return (
    <div className="panel">
      <h3 className="panel__title">Summary</h3>

      {s.tldr && (
        <div className="summary-tldr">
          <span className="summary-tldr__label">TL;DR</span>
          {s.tldr}
        </div>
      )}

      {keyTopics.length > 0 && (
        <div className="summary-section">
          <h4 className="summary-section__title">Key Topics</h4>
          <ul className="summary-list">
            {keyTopics.map((t, i) => (
              <li key={`${t.topic}-${i}`}>
                {typeof t.timestampMs === 'number' && (
                  <span
                    className="summary-topic-time"
                    onClick={() => seek(t.timestampMs!)}
                    role="button"
                  >
                    {formatMsClock(t.timestampMs)}
                  </span>
                )}
                {t.topic}
              </li>
            ))}
          </ul>
        </div>
      )}

      {nextSteps.length > 0 && (
        <div className="summary-section">
          <h4 className="summary-section__title">Next Steps</h4>
          <ul className="summary-list">
            {nextSteps.map((n, i) => (
              <li key={`${n}-${i}`}>{n}</li>
            ))}
          </ul>
        </div>
      )}

      {seatWork.length > 0 && <SeatWorkList items={seatWork} />}
    </div>
  );
}

function SeatWorkList({ items }: { items: NonNullable<MeetingDetail['seatWork']> }): JSX.Element {
  return (
    <div className="summary-section">
      <h4 className="summary-section__title">What AI did</h4>
      <ul className="summary-list">
        {items.map((item) => (
          <li key={item.id}>
            {item.summary}
            {item.links.map((link) => (
              <a key={link.url} href={link.url} target="_blank" rel="noreferrer"> {link.label}</a>
            ))}
          </li>
        ))}
      </ul>
    </div>
  );
}

interface SpeakerSummary { label: string; name: string | null }

function uniqueSpeakers(transcript: TranscriptLine[]): SpeakerSummary[] {
  const seen = new Map<string, SpeakerSummary>();
  for (const line of transcript) {
    if (!seen.has(line.speakerLabel)) {
      seen.set(line.speakerLabel, { label: line.speakerLabel, name: line.speakerName });
    }
  }
  return Array.from(seen.values());
}

function findActive(transcript: TranscriptLine[], ms: number): number {
  if (ms <= 0) return -1;
  for (let i = transcript.length - 1; i >= 0; i--) {
    const line = transcript[i];
    if (line && ms >= line.startMs) return i;
  }
  return -1;
}
